/* eslint-disable @typescript-eslint/naming-convention */

import { format, isEmpty } from "@swiggy-private/js-utils";
import { UserAddress, IIrctcPayload, IIrctcNonPnr } from "@swiggy-private/cart-types";
import { IIrctcDeliveryDetailsData } from "@swiggy-private/cart-ui";

import {
    IRCTC_STATION_CHANGE_URL,
    IRCTC_START_DATE_FORMAT,
    IRCTC_SLA_PREFIX_ORIGINAL,
    IRCTC_SLA_PREFIX_REPLACEMENT,
    IRCTC_DELIVERY_AT_STATION_TEXT,
    IRCTC_SEAT_LABEL,
    IRCTC_SEAT_LABEL_FALLBACK,
    IRCTC_TRAIN_NUMBER_SUFFIX,
    IRCTC_TRAIN_NAME_SUFFIX,
    IRCTC_ORDER_TYPE_SUBTITLE,
} from "@cart-client/constants/Irctc";

import { ReviewedCart } from "@cart-client/interfaces/Cart";

export const getIrctcStationChangeUrl = (): string => {
    if (typeof window !== "undefined") {
        return window?.__IRCTC_STATION_CHANGE_URL__ || IRCTC_STATION_CHANGE_URL;
    }

    return IRCTC_STATION_CHANGE_URL;
};

const formatDate = (date: Date, pattern: string): string => {
    const tokens: Record<string, string> = {
        yyyy: String(date.getFullYear()),
        MM: String(date.getMonth() + 1).padStart(2, "0"),
        dd: String(date.getDate()).padStart(2, "0"),
    };

    return pattern.replace(/yyyy|MM|dd/g, (token) => tokens[token]);
};

export const buildIrctcStationChangeUrl = (irctcData?: IIrctcPayload): string => {
    const params = new URLSearchParams();
    const pnrNumber = irctcData?.pnr_number;

    if (!isEmpty(pnrNumber)) {
        params.set("pnr", pnrNumber);
    } else {
        const trainStartDate = irctcData?.train_start_date ?? formatDate(new Date(), IRCTC_START_DATE_FORMAT);

        params.set("trainNumber", irctcData?.train_number ?? "");
        params.set("trainStartDate", trainStartDate);
        params.set("sourceStationCode", irctcData?.source_station_code ?? "");
        params.set("trainName", irctcData?.train_name ?? "");
    }

    return `${getIrctcStationChangeUrl()}?${params.toString()}`;
};

export const getIrctcPnrToolTipText = (reviewedCart: ReviewedCart | null): string | undefined => {
    return reviewedCart?.data?.irctc_info?.pnr_tool_tip_data?.text;
};

export const getIrctcDeliveryDetailsData = (
    reviewedCart: ReviewedCart | null,
    selectedAddress: UserAddress | null,
): IIrctcDeliveryDetailsData | undefined => {
    if (isEmpty(reviewedCart)) {
        return undefined;
    }

    const {
        coach,
        berth,
        train_number: trainNumber,
        train_name: trainName,
        mobile_number: mobileNumber,
        station_name: stationName,
    } = reviewedCart.data?.irctc_data ?? {};

    const selectedSlaMessage =
        selectedAddress?.sla_message ??
        reviewedCart.data?.addresses?.find((address) => address.address_id === selectedAddress?.address_id)
            ?.sla_message;
    const title = selectedSlaMessage?.replace(IRCTC_SLA_PREFIX_ORIGINAL, IRCTC_SLA_PREFIX_REPLACEMENT) ?? "";

    const subtitle = stationName ? format(IRCTC_DELIVERY_AT_STATION_TEXT, stationName) : "";

    const seatNumber = coach && berth ? format(IRCTC_SEAT_LABEL, coach, berth) : IRCTC_SEAT_LABEL_FALLBACK;

    const trainNumberSuffix = trainNumber ? format(IRCTC_TRAIN_NUMBER_SUFFIX, trainNumber) : "";
    const trainNameSuffix = trainName ? format(IRCTC_TRAIN_NAME_SUFFIX, trainName) : "";
    const orderTypeSubtitle = format(IRCTC_ORDER_TYPE_SUBTITLE, seatNumber, trainNumberSuffix, trainNameSuffix);

    return {
        title,
        subtitle,
        orderTypeTitle: "",
        orderTypeSubtitle,
        mobileNumber: mobileNumber || "",
        background: reviewedCart.data?.irctc_info?.presentation_info?.background,
        widgetImage: reviewedCart.data?.irctc_info?.presentation_info?.widget_image,
    };
};

export const getNonPnrIrctcData = (reviewedCart: ReviewedCart | null): IIrctcNonPnr | undefined =>
    reviewedCart?.data?.irctc_info?.non_pnr;

export const getNonPnrIrctcIdentifier = (reviewedCart: ReviewedCart | null): boolean =>
    !isEmpty(reviewedCart?.data?.irctc_info?.non_pnr);
